import { Seo } from '@wwtr/components/layout';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import React from 'react';
import { useQuery } from '@apollo/client';
import {
  FacilityDetailQuery,
  FacilityEntryFragment,
  FacilityEntryOrganizationFragment,
  FacilityEntryOrganizationLocationFragment,
} from '@wwtr/generated';
import {
  BreadcrumbsItem,
  Button,
  Container,
  Heading,
  Link,
  PageHeader,
  RichText,
  Section,
  Skeleton,
  Tile,
} from '@wwtr/components/ui';
import FACILITY_DETAIL_QUERY from '@wwtr/gql/queries/facility-detail.gql';
import { getNetworkStatusDetails } from '@wwtr/libs/apollo/utils';
import { HeartIcon, ShieldCheckIcon } from '@heroicons/react/outline';
import { ArrowRightIcon } from '@heroicons/react/solid';
import { FAVORITE_TYPE, useFavorites } from '@wwtr/libs/contexts';

const OrganizationDetailPage = () => {
  const router = useRouter();
  const { toggleFavorite, favorites } = useFavorites();

  const onClickFavorite = ($event, id: string) => {
    toggleFavorite(FAVORITE_TYPE.FACILITY, id);
  };

  const { asPath } = router;

  const uri = decodeURIComponent(asPath).substring(1); // decode + strip leading slash

  const {
    data: organizationDetailData,
    networkStatus: organizationDetailNetworkStatus,
    error: organizationDetailError,
  } = useQuery<FacilityDetailQuery>(FACILITY_DETAIL_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      uri: [uri],
    },
    skip: !uri,
  });

  const {
    isLoading: organizationDetailIsLoading,
    isReady: organizationDetailIsReady,
  } = getNetworkStatusDetails(organizationDetailNetworkStatus);

  // if (organizationDetailIsReady && (organizationDetailError || !organizationDetailData?.entry)) {
  //   return <App404 />;
  // }

  let breadCrumbs: BreadcrumbsItem[] = [
    {
      url: '/zoek/organisaties',
      label: 'Organisaties',
    },
  ];

  const entry = organizationDetailData?.entry as FacilityEntryFragment;
  const organization =
    (entry?.organization[0] as FacilityEntryOrganizationFragment) ?? null;
  const isFavorite = favorites?.facilities?.includes(entry?.id);

  if (organizationDetailIsReady) {
    breadCrumbs.push({
      url: `/${organization?.uri}`,
      label: organization?.title,
      shorten: true,
    });

    // Always push own entry
    breadCrumbs.push({
      label: entry?.title,
      shorten: true,
    });
  }

  return (
    <article>
      <Seo title={entry?.title} />
      <PageHeader
        title={entry?.title}
        isLoading={organizationDetailIsLoading}
        breadCrumbs={breadCrumbs}
      />

      <Section>
        <Container>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-8 xl:col-span-7">
              {organizationDetailIsLoading ? (
                <div className="space-y-6 mt-4">
                  <Skeleton className="w-full h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-7/12 h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-9/12 h-5 lg:h-6"></Skeleton>
                  <Skeleton className="w-1/3 h-5 lg:h-6"></Skeleton>
                </div>
              ) : (
                <div>
                  <div className="mb-16 text-2xl leading-snug lg:text-3xl lg:leading-snug text-gray-700">
                    <RichText content={entry?.description} />
                  </div>
                  {entry?.details && (
                    <Tile
                      variant="secondary"
                      size="md"
                      hasShadow
                      className="mb-8"
                    >
                      <Heading
                        level={4}
                        className="mb-4 flex items-center"
                        variant="black"
                      >
                        <ShieldCheckIcon className="w-8 h-8 mr-2" />
                        <span>Bijzonderheden</span>
                      </Heading>
                      <RichText content={entry?.details} />
                    </Tile>
                  )}
                </div>
              )}
            </div>
            <div className="col-span-12 lg:col-span-4 xl:col-start-9">
              {!organizationDetailIsLoading && (
                <div className="sticky top-32">
                  <Tile variant="white" size="md" hasShadow className="mb-8">
                    <Heading level={4} className="mb-4" variant="gray-800">
                      Aangeboden door
                    </Heading>

                    <Heading level={5} className="mb-4" variant="powerblue">
                      <NextLink href={`/${organization?.uri}`} passHref>
                        <Link noWrap={false}>{organization?.title}</Link>
                      </NextLink>
                    </Heading>
                    <p className="mb-8 text-md leading-snug lg:text-md lg:leading-snug text-gray-700">
                      {organization?.locations.length > 1 ? (
                        <span>{organization?.locations.length} locaties</span>
                      ) : (
                        <span>
                          {
                            (
                              organization
                                ?.locations[0] as FacilityEntryOrganizationLocationFragment
                            )?.visitAddress?.address
                          }
                        </span>
                      )}
                    </p>
                    <NextLink href={`/${organization?.uri}`} passHref>
                      <Button
                        as="a"
                        color="primary"
                        appearance="outline"
                        withIcon
                        size="sm"
                        title={`Lees meer over ${organization?.title}`}
                      >
                        <span>Lees meer</span>
                        <ArrowRightIcon className="w-4" />
                      </Button>
                    </NextLink>
                  </Tile>
                  <div className="print:hidden">
                    <Button
                      appearance={isFavorite ? 'solid' : 'outline'}
                      color="hotpink"
                      fullWidth={true}
                      withIcon
                      className="mb-4"
                      onClick={$event => onClickFavorite($event, entry.id)}
                    >
                      <HeartIcon className="w-6" />
                      {isFavorite ? (
                        <span>Verwijder uit favorieten</span>
                      ) : (
                        <span>Opslaan in favorieten</span>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </Section>

      <Section as="aside" className="bg-powerblue print:hidden">
        <Container>
          <Heading level={2} variant="white" className="mb-12">
            Niet gevonden wat u zocht?
          </Heading>
          <div className="inline-flex flex-wrap items-baseline">
            <p className="text-white opacity-80 mb-4 mr-4">Bekijk meer:</p>
            {entry?.serviceType.map((category, index) => (
              <div key={`category${index}`} className="mb-4 mr-4">
                <NextLink href={`/zoek/aanbod?t=${category.id}`}>
                  <Button
                    as="a"
                    appearance="outline"
                    color="white"
                    withIcon
                    size="sm"
                    href={`/zoek/aanbod?t=${category.id}`}
                    title={`Lees meer over ${category.title}`}
                  >
                    {category.title}
                  </Button>
                </NextLink>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </article>
  );
};

// export async function getStaticPaths() {
//   return {
//     paths: [
//       { params: { ... } } // See the "paths" section below
//     ],
//     fallback: true, false, or 'blocking' // See the "fallback" section below
//   };
// }

export default OrganizationDetailPage;
